import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { userUpdatePassword } from '../../redux/authSlice';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { Eye, EyeOff, Check, AlertTriangle } from 'lucide-react';
import useAxiosAuth from '../../hooks/useAxiosAuth';

function PasswordTab({ doctor }) { // Assuming docId is passed as a prop
    const [currentPassword, setCurrentPassword] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const { t } = useTranslation(['doctorpassword', 'common']);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({ currentPassword: '', password: '', confirmPassword: '' });

    const dispatch = useDispatch();
    const axiosInstance = useAxiosAuth();
    const { isLoading, error } = useSelector((state) => state.auth);

    const validatePassword = (pwd) => {
        const minLength = pwd.length >= 8;
        const maxLength = pwd.length <= 25;
        const hasUpperCase = /[A-Z]/.test(pwd);
        const hasLowerCase = /[a-z]/.test(pwd);
        const hasNumber = /[0-9]/.test(pwd);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

        if (!minLength) return t('doctorpassword.text1', { ns: 'doctorpassword' });
        if (!maxLength) return t('validation.password_too_long_25', { ns: 'common' });
        if (!hasUpperCase) return t('doctorpassword.text2', { ns: 'doctorpassword' });
        if (!hasLowerCase) return t('doctorpassword.text3', { ns: 'doctorpassword' });
        if (!hasNumber) return t('doctorpassword.text4', { ns: 'doctorpassword' });
        if (!hasSpecialChar) return t('doctorpassword.text5', { ns: 'doctorpassword' });
        return '';
    };

    const getPasswordStrength = (pwd) => {
        if (!pwd) return '';
        const minLength = pwd.length >= 8;
        const maxLength = pwd.length <= 25;
        const hasUpperCase = /[A-Z]/.test(pwd);
        const hasLowerCase = /[a-z]/.test(pwd);
        const hasNumber = /[0-9]/.test(pwd);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

        // Password is strong if it meets all requirements
        if (minLength && maxLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar) {
            return t('doctorPassword.strong');
        }
        return t('doctorPassword.weak');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Client-side validation
        const currentPasswordError = !currentPassword ? t('doctorpassword.current_required', { ns: 'doctorpassword' }) : '';
        const passwordError = validatePassword(password);
        const sameAsCurrentError = password && currentPassword && password === currentPassword 
            ? t('doctorpassword.same_as_current', { ns: 'doctorpassword' })
            : '';
        const confirmPasswordError =
            password !== confirmPassword
                ? t('doctorpassword.notmatch', { ns: 'doctorpassword' })
                : '';

        if (currentPasswordError || passwordError || sameAsCurrentError || confirmPasswordError) {
            setErrors({
                currentPassword: currentPasswordError,
                password: sameAsCurrentError || passwordError,
                confirmPassword: confirmPasswordError,
            });
            return;
        }

        // SweetAlert confirmation BEFORE API hit
        const result = await Swal.fire({
            title: t('doctorpassword.confirm_title'),
            text: t('doctorpassword.confirm_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: t('doctorpassword.yes'),
            cancelButtonText: t('doctorpassword.no'),
        });

        if (result.isConfirmed) {
            try {
                const docId = doctor.doctorId;
                await dispatch(
                    userUpdatePassword({
                        docId,
                        currentPassword,
                        newPassword: password,
                        confirmPassword,
                        axiosInstance,
                    })
                ).unwrap();

                // Reset form
                setCurrentPassword('');
                setPassword('');
                setConfirmPassword('');
                setErrors({ currentPassword: '', password: '', confirmPassword: '' });

                // Success alert
                toast.success(t('doctorpassword.success_text'));
            } catch (err) {
                // Error alert
                const errorMessage = err || t('doctorpassword.error_text');
                toast.warn(errorMessage);
            }
        }
    };

    const handleCancel = () => {
        setCurrentPassword('');
        setPassword('');
        setConfirmPassword('');
        setErrors({ currentPassword: '', password: '', confirmPassword: '' });
    };

    return (
        <div data-testid="doctor-password-tab-container" className="mt-4">
            {/* Header Row */}
            <div data-testid="doctor-password-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h2 data-testid="doctor-password-title" className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('doctorpassword.heading', { ns: 'doctorpassword' })}
                </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
                <div className="relative">
                    <label data-testid="doctor-current-password-label" htmlFor="input-current-password" className="block text-sm font-semibold text-gray-600 mb-2">
                        {t('doctorpassword.current', { ns: 'doctorpassword' })} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <input
                            data-testid="doctor-current-password-input"
                            id="input-current-password"
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={currentPassword}
                            onChange={(e) => {
                                const newCurrentPassword = e.target.value;
                                setCurrentPassword(newCurrentPassword);
                                if (errors.currentPassword) {
                                    setErrors({ ...errors, currentPassword: '' });
                                }
                                // Check if new password is same as current password
                                if (password && password === newCurrentPassword) {
                                    setErrors(prev => ({
                                        ...prev,
                                        password: t('doctorpassword.same_as_current', { ns: 'doctorpassword' })
                                    }));
                                } else if (errors.password && password !== newCurrentPassword) {
                                    // Clear the error if passwords are now different
                                    const passwordError = validatePassword(password);
                                    setErrors(prev => ({
                                        ...prev,
                                        password: passwordError
                                    }));
                                }
                            }}
                            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.currentPassword ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                            placeholder={t('doctorpassword.currenttext', { ns: 'doctorpassword' })}
                            disabled={isLoading}
                        />
                        <button
                            data-testid="doctor-toggle-current-password"
                            id="btn-toggle-current-password"
                            type="button"
                            className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer text-gray-400 hover:text-[#4ABDBD] transition-colors duration-300"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            disabled={isLoading}
                        >
                            {showCurrentPassword ? (
                                <Eye className="h-5 w-5" />
                            ) : (
                                <EyeOff className="h-5 w-5" />
                            )}
                        </button>
                    </div>
                    {errors.currentPassword && (
                        <p data-testid="doctor-current-password-error" className="mt-2 text-xs text-red-500 font-medium">{errors.currentPassword}</p>
                    )}
                </div>

                <div className="relative">
                    <label data-testid="doctor-new-password-label" htmlFor="input-password" className="block text-sm font-semibold text-gray-600 mb-2">
                        {t('doctorpassword.new', { ns: 'doctorpassword' })} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <input
                            data-testid="doctor-new-password-input"
                            id="input-password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => {
                                const newPassword = e.target.value;
                                
                                // Enforce max length
                                if (newPassword.length > 25) {
                                    return;
                                }
                                
                                setPassword(newPassword);
                                
                                // Clear password error if exists
                                if (errors.password) {
                                    setErrors({ ...errors, password: '' });
                                }
                                
                                // Check if new password is same as current password
                                if (currentPassword && newPassword === currentPassword) {
                                    setErrors(prev => ({
                                        ...prev,
                                        password: t('doctorpassword.same_as_current', { ns: 'doctorpassword' })
                                    }));
                                } else if (errors.password && newPassword !== currentPassword) {
                                    // Validate password if it's different from current
                                    const passwordError = validatePassword(newPassword);
                                    setErrors(prev => ({
                                        ...prev,
                                        password: passwordError
                                    }));
                                }
                                
                                // Check confirm password match if confirm password has value
                                if (confirmPassword && newPassword !== confirmPassword) {
                                    setErrors(prev => ({
                                        ...prev,
                                        confirmPassword: t('doctorpassword.notmatch', { ns: 'doctorpassword' })
                                    }));
                                } else if (confirmPassword && newPassword === confirmPassword) {
                                    setErrors(prev => ({
                                        ...prev,
                                        confirmPassword: ''
                                    }));
                                }
                            }}
                            maxLength={25}
                            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.password ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                            placeholder={t('doctorpassword.newtext', { ns: 'doctorpassword' })}
                            disabled={isLoading}
                        />
                        <button
                            data-testid="doctor-toggle-new-password"
                            id="btn-toggle-password"
                            type="button"
                            className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer text-gray-400 hover:text-[#4ABDBD] transition-colors duration-300"
                            onClick={() => setShowPassword(!showPassword)}
                            disabled={isLoading}
                        >
                            {showPassword ? (
                                <Eye className="h-5 w-5" />
                            ) : (
                                <EyeOff className="h-5 w-5" />
                            )}
                        </button>
                    </div>
                    {errors.password && (
                        <p data-testid="doctor-new-password-error" className="mt-2 text-xs text-red-500 font-medium">{errors.password}</p>
                    )}
                    {password && !errors.password && (
                        <p data-testid="doctor-password-strength" className={`text-xs mt-1 flex items-center gap-1 ${
                            getPasswordStrength(password) === t('doctorPassword.strong')
                                ? 'text-green-600'
                                : 'text-amber-600'
                        }`}>
                            {getPasswordStrength(password) === t('doctorPassword.strong')
                                ? <Check className="w-3 h-3" />
                                : <AlertTriangle className="w-3 h-3" />}
                            {t('doctorPassword.password_strength')} {getPasswordStrength(password)}
                        </p>
                    )}
                </div>

                <div className="relative">
                    <label data-testid="doctor-confirm-password-label" htmlFor="input-confirm-password" className="block text-sm font-semibold text-gray-600 mb-2">
                        {t('doctorpassword.confirm', { ns: 'doctorpassword' })} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <input
                            data-testid="doctor-confirm-password-input"
                            id="input-confirm-password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => {
                                const newConfirmPassword = e.target.value;
                                
                                // Enforce max length
                                if (newConfirmPassword.length > 25) {
                                    return;
                                }
                                
                                setConfirmPassword(newConfirmPassword);
                                
                                // Clear error if exists
                                if (errors.confirmPassword) {
                                    setErrors({ ...errors, confirmPassword: '' });
                                }
                                
                                // Check if confirm password matches new password
                                if (password && newConfirmPassword !== password) {
                                    setErrors(prev => ({
                                        ...prev,
                                        confirmPassword: t('doctorpassword.notmatch', { ns: 'doctorpassword' })
                                    }));
                                } else if (password && newConfirmPassword === password) {
                                    setErrors(prev => ({
                                        ...prev,
                                        confirmPassword: ''
                                    }));
                                }
                            }}
                            maxLength={25}
                            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.confirmPassword ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                            placeholder={t('doctorpassword.confirmtext', { ns: 'doctorpassword' })}
                            disabled={isLoading}
                        />
                        <button
                            data-testid="doctor-toggle-confirm-password"
                            id="btn-toggle-confirm-password"
                            type="button"
                            className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer text-gray-400 hover:text-[#4ABDBD] transition-colors duration-300"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            disabled={isLoading}
                        >
                            {showConfirmPassword ? (
                                <Eye className="h-5 w-5" />
                            ) : (
                                <EyeOff className="h-5 w-5" />
                            )}
                        </button>
                    </div>
                    {errors.confirmPassword && (
                        <p data-testid="doctor-confirm-password-error" className="mt-2 text-xs text-red-500 font-medium">{errors.confirmPassword}</p>
                    )}
                </div>
            </div>

            <div className="flex justify-center gap-4 mt-8">
                <button
                    data-testid="doctor-password-update-button"
                    id="btn-update-password"
                    type="button"
                    onClick={handleSubmit}
                    className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold relative overflow-hidden text-sm ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'}`}
                    disabled={isLoading}
                >
                    <span data-testid="update-text">
                        {isLoading ? t('doctorpassword.updating', { ns: 'doctorpassword' }) : t('doctorpassword.update', { ns: 'doctorpassword' })}
                    </span>
                </button>
                <button
                    data-testid="doctor-password-cancel-button"
                    id="btn-cancel"
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                    disabled={isLoading}
                >
                    {t('doctorpassword.cancel', { ns: 'doctorpassword' })}
                </button>
            </div>
        </div>
    );
}

export default PasswordTab;


{/* <input id="input-password" type="password" class="..." placeholder="..." />
<button id="btn-toggle-password" type="button" class="...">...</button>
<input id="input-confirm-password" type="password" class="..." placeholder="..." />
<button id="btn-toggle-confirm-password" type="button" class="...">...</button>
<button id="btn-update-password" type="button" class="...">...</button>
<button id="btn-cancel" type="button" class="...">...</button> */}